import { useEffect, useMemo } from 'react';
import { type UseFormReturn, useWatch } from 'react-hook-form';

import {
  APPOINTMENT_NO_PRACTITIONER,
  type AppointmentCreateFormData,
  type AppointmentCreatePractitioner,
  canPractitionerPerformService,
  practitionersForService,
} from './appointment-create-form';

/**
 * The first service's team-member list: only the people who perform it (or
 * everyone, when nobody is assigned to it).
 *
 * Picking a service the chosen person does not do clears them back to "Any
 * team member" rather than booking a pairing the clinic ruled out — but only
 * once we can SEE they do not do it (the team list has loaded), so a slot's
 * seeded practitioner is never dropped while the list is still in flight.
 *
 * Shared by the desktop dialog and the mobile details step.
 */
export function useFirstServicePractitioners<
  P extends AppointmentCreatePractitioner,
>(form: UseFormReturn<AppointmentCreateFormData>, practitioners: P[]) {
  const serviceId = useWatch({ control: form.control, name: 'serviceId' });
  const practitionerId = useWatch({
    control: form.control,
    name: 'practitionerId',
  });

  const eligible = useMemo(
    () => practitionersForService(practitioners, serviceId),
    [practitioners, serviceId]
  );

  useEffect(() => {
    if (!serviceId) return;
    const current = form.getValues('practitionerId');
    const known = practitioners.some((p) => p.id === current);
    if (
      known &&
      !canPractitionerPerformService(practitioners, serviceId, current)
    ) {
      form.setValue('practitionerId', APPOINTMENT_NO_PRACTITIONER);
    }
  }, [serviceId, practitioners, form]);

  return { eligible, practitionerId };
}
